import { type StockWriteOff, WRITE_OFF_REASON_LABELS } from "@station/contracts";
import { Alert, Button, Dialog, FormField, MoneyText, SkeletonRows, Textarea, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Send, Undo2, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import { formatStockQuantity, writeOffActions } from "../../../../features/market/market-values.js";
import { useMarketPermissions, useRefreshMarket, useWriteOff } from "../../../../features/market/queries.js";
import {
  crumbLink,
  Field,
  LedgerStatus,
  MarketPage,
  TableFrame,
  useMarketStations,
  WriteOffStatusBadge,
} from "../../../../features/market/shared.js";
import { WriteOffDialog } from "../../../../features/market/write-off-dialog.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/market/fireler/$id")({ component: WriteOffDetailRoute });

function WriteOffDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const writeOff = useWriteOff(id);
  const title = writeOff.data?.writeOffNo ?? t("market.writeOffs.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketWriteOffs"), "/market/fireler"), { label: title }]}
    >
      {() =>
        writeOff.error ? (
          <Alert tone="danger">{errorMessage(writeOff.error)}</Alert>
        ) : !writeOff.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <WriteOffDetail writeOff={writeOff.data} />
        )
      }
    </MarketPage>
  );
}

function WriteOffDetail({ writeOff }: { writeOff: StockWriteOff }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const stations = useMarketStations(writeOff.companyId);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const actions = writeOffActions(writeOff.status);
  const canManage = permissions.canManageStock;

  const act = useMutation({
    mutationFn: async (action: "submit" | "reopen" | "cancel") => {
      const params = { path: { id: writeOff.id } };
      if (action === "submit")
        return (
          await api.POST("/market/write-offs/{id}/submit", { params, body: { version: writeOff.version } })
        ).data!;
      if (action === "reopen")
        return (
          await api.POST("/market/write-offs/{id}/reopen", { params, body: { version: writeOff.version } })
        ).data!;
      return (
        await api.POST("/market/write-offs/{id}/cancel", {
          params,
          body: { version: writeOff.version, reason },
        })
      ).data!;
    },
    onSuccess: async (saved, action) => {
      setCancelling(false);
      setReason("");
      await refresh();
      // Onaya gönderimde politika yoksa belge hemen KAYDEDİLİR: kullanıcıya hangisinin olduğu söylenir.
      const key =
        action === "submit"
          ? (saved as StockWriteOff).status === "POSTED"
            ? "posted"
            : "submitted"
          : action;
      toast({ tone: "success", title: t(`market.writeOffs.done.${key}`) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{writeOff.writeOffNo}</span>
        <WriteOffStatusBadge status={writeOff.status} />
        <span className="text-sm text-muted-foreground">{WRITE_OFF_REASON_LABELS[writeOff.reason]}</span>
        {canManage ? (
          <div className="ms-auto flex flex-wrap gap-2">
            {actions.edit ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil aria-hidden="true" />
                {t("common.edit")}
              </Button>
            ) : null}
            {actions.submit ? (
              <Button
                loading={act.isPending && act.variables === "submit"}
                onClick={() => act.mutate("submit")}
              >
                <Send aria-hidden="true" />
                {t("market.writeOffs.submit")}
              </Button>
            ) : null}
            {actions.reopen ? (
              <Button
                variant="outline"
                loading={act.isPending && act.variables === "reopen"}
                onClick={() => act.mutate("reopen")}
              >
                <Undo2 aria-hidden="true" />
                {t("market.writeOffs.reopen")}
              </Button>
            ) : null}
            {actions.cancel ? (
              <Button variant="destructive" onClick={() => setCancelling(true)}>
                <X aria-hidden="true" />
                {t("market.writeOffs.cancel")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {writeOff.status === "PENDING_APPROVAL" ? (
        <Alert tone="info">{t("market.writeOffs.pendingHint")}</Alert>
      ) : null}
      {writeOff.status === "REJECTED" ? (
        <Alert tone="warning">{t("market.writeOffs.rejectedHint")}</Alert>
      ) : null}

      <Section title={t("market.writeOffs.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.fields.warehouse")}>
            {writeOff.stationName} · <span className="font-mono">{writeOff.warehouseCode}</span>
          </Field>
          <Field label={t("market.writeOffs.fields.reason")}>
            {WRITE_OFF_REASON_LABELS[writeOff.reason]}
          </Field>
          <Field label={t("market.writeOffs.fields.lossKey")}>
            {t(`market.lossKey.${writeOff.lossKey}`)}
          </Field>
          <Field
            label={
              writeOff.status === "POSTED"
                ? t("market.writeOffs.fields.totalValue")
                : t("market.writeOffs.fields.estimatedValue")
            }
          >
            <MoneyText value={writeOff.totalValue} />
          </Field>
          <Field label={t("market.writeOffs.fields.postedAt")}>{formatDateTime(writeOff.postedAt)}</Field>
          <Field label={t("market.fields.ledger")}>
            <LedgerStatus status={writeOff.ledgerStatus} journalEntryId={writeOff.journalEntryId} />
          </Field>
          <Field label={t("market.fields.createdAt")}>{formatDateTime(writeOff.createdAt)}</Field>
        </dl>
        {writeOff.note ? <p className="text-sm">{writeOff.note}</p> : null}
      </Section>

      <Section title={t("market.writeOffs.lines")}>
        <TableFrame label={t("market.writeOffs.lines")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("market.fields.product")}</Th>
              <Th>{t("market.fields.lotNo")}</Th>
              <Th align="right">{t("market.fields.quantity")}</Th>
              <Th align="right">{t("market.fields.value")}</Th>
              <Th>{t("market.fields.note")}</Th>
            </tr>
          </thead>
          <tbody>
            {writeOff.lines.map((line) => (
              <tr key={line.id} className="border-t">
                <td className="px-3 py-2">
                  <span className="grid gap-0.5">
                    <span>{line.productName}</span>
                    <span className="font-mono text-xs text-muted-foreground">{line.productCode}</span>
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {line.lotId === null
                    ? t("market.lines.fefoShort")
                    : (line.lotNo ?? t("market.lots.noLotNo"))}
                </td>
                <td className="px-3 py-2 text-right tabular">{formatStockQuantity(line.quantity)}</td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={line.value} />
                </td>
                <td className="px-3 py-2">{line.note ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </Section>

      {writeOff.approvalRequestId ? (
        <Section title={t("market.writeOffs.approval")}>
          <ApprovalRequestPanel requestId={writeOff.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      {canManage && actions.edit ? (
        <WriteOffDialog
          open={editing}
          onOpenChange={setEditing}
          companyId={writeOff.companyId}
          stations={stations.stations}
          defaultStationId={writeOff.stationId}
          writeOff={writeOff}
          onSaved={() => undefined}
        />
      ) : null}

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("market.writeOffs.cancelTitle")}
        description={t("market.writeOffs.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("market.fields.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={300}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              loading={act.isPending && act.variables === "cancel"}
              disabled={reason.trim().length < 3}
            >
              {t("market.writeOffs.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
